/**
 * svgInsolation — pure geometry / time helpers for the Product Insolation mode.
 *
 * Ported from legacy insolation_script.js (vanilla AplusInsolation MVP).
 * Pure functions only — no DOM mutations, no React, easy to unit-test.
 *
 * Angle space conventions:
 *   - `insolationAngleDeg` ∈ [90, 270]; 90 = 06:00, 180 = 12:00, 270 = 18:00.
 *   - `azimuthDeg` is cartesian CCW from +X (default 90 = north up).
 *   - Patches are drawn in Y-UP space (apartment_tier scale(1,-1)). The
 *     directional ray angle for that space is:
 *         rayAngle = azimuthDeg - insolationAngleDeg + 180
 */

import { INSOLATION_DEFAULTS } from '../constants/overlayDefaults.js'
import { getPathsBoundaryPoints } from '../../../shared/utils/svgUtils.js'

const DEFAULT_AZIMUTH_DEG = 90 // assume north = up

// ── Angle / time conversions ─────────────────────────────────────────────────

/** Decimal hours [6..18] for a given slider angle. */
export function angleToHour(angleDeg) {
  return (angleDeg - 90) / 15 + 6
}

/** Inverse of {@link angleToHour}. */
export function hourToAngle(hour) {
  return (hour - 6) * 15 + 90
}

/**
 * Format an insolation slider angle as a "HH:MM" wall-clock string,
 * rounded to the nearest 10 minutes.
 */
export function angleToTime(angleDeg) {
  const totalMinutes = ((angleDeg - 90) / 180) * (12 * 60)
  const rounded = Math.round(totalMinutes / 10) * 10
  const hours = Math.floor(rounded / 60) + 6
  const minutes = Math.abs(rounded % 60)
  const hh = String(hours).padStart(2, '0')
  const mm = String(minutes).padStart(2, '0')
  return `${hh}:${mm}`
}

export function clampInsolationAngle(angleDeg, defaults = INSOLATION_DEFAULTS) {
  const value = Number(angleDeg)
  if (!Number.isFinite(value)) return defaults.initialAngleDeg
  if (value < defaults.minAngleDeg) return defaults.minAngleDeg
  if (value > defaults.maxAngleDeg) return defaults.maxAngleDeg
  return value
}

function _normalize360(angleDeg) {
  return ((angleDeg % 360) + 360) % 360
}

/**
 * Map slider angle + product azimuth → ray direction angle in Y-UP cartesian space.
 */
export function getDirectionalSunAngle(insolationAngleDeg, azimuthDeg) {
  const az = Number.isFinite(Number(azimuthDeg)) ? Number(azimuthDeg) : DEFAULT_AZIMUTH_DEG
  return _normalize360(az - insolationAngleDeg + 180)
}

// ── Geometry — boundary parsing ──────────────────────────────────────────────

// Re-exported from shared utils so callers that already import from here keep working.
export { getPathsBoundaryPoints as getBoundaryPoints }

/**
 * Parse a subset of SVG `transform` (translate + rotate) into a flat record.
 */
export function parseTransform(transformText) {
  const out = { tx: 0, ty: 0, rotate: 0, cx: 0, cy: 0 }
  if (!transformText) return out

  const t = transformText.match(/translate\(\s*([-+]?\d*\.?\d+)\s*(?:[,\s]\s*([-+]?\d*\.?\d+))?\s*\)/i)
  if (t) {
    out.tx = Number(t[1]) || 0
    out.ty = Number(t[2]) || 0
  }
  const r = transformText.match(/rotate\(\s*([-+]?\d*\.?\d+)\s*(?:[,\s]\s*([-+]?\d*\.?\d+)\s*[,\s]\s*([-+]?\d*\.?\d+))?\s*\)/i)
  if (r) {
    out.rotate = Number(r[1]) || 0
    out.cx = Number(r[2]) || 0
    out.cy = Number(r[3]) || 0
  }

  return out
}

function _toRadians(deg) {
  return (deg * Math.PI) / 180
}

function _rotatePoint(x, y, deg, cx, cy) {
  const rad = _toRadians(deg)
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return [
    cos * (x - cx) - sin * (y - cy) + cx,
    sin * (x - cx) + cos * (y - cy) + cy,
  ]
}

export function transformPoint(x, y, transform) {
  const [rx, ry] = _rotatePoint(x, y, transform.rotate, transform.cx, transform.cy)
  return [rx + transform.tx, ry + transform.ty]
}

/**
 * Longest edge of a closed polygon (the sill outline). The sun patch starts
 * at the dominant edge and extends inward by `patchDistance`.
 *
 * @param {[number, number][]} points
 * @returns {[[number, number], [number, number]] | null}
 */
export function getDominantEdge(points) {
  if (!points || points.length < 2) return null
  let best = null
  let bestLen = -1
  for (let i = 0; i < points.length; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    const len = Math.sqrt(dx * dx + dy * dy)
    if (len > bestLen) {
      bestLen = len
      best = [a, b]
    }
  }
  return best
}

/**
 * Returns the two longest edges of a closed polygon (the two long sides of a
 * rectangular sill), sorted by length descending. Used to disambiguate the
 * outer (window) edge from the inner edge: see {@link pickOuterEdge}.
 *
 * @param {[number, number][]} points
 * @returns {[[number, number], [number, number]][]}
 */
export function getTopTwoLongEdges(points) {
  if (!points || points.length < 2) return []
  const edges = []
  for (let i = 0; i < points.length; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    edges.push({ a, b, len: Math.sqrt(dx * dx + dy * dy) })
  }
  edges.sort((p, q) => q.len - p.len)
  return edges.slice(0, 2).map(({ a, b }) => [a, b])
}

function _midpoint(seg) {
  return [(seg[0][0] + seg[1][0]) / 2, (seg[0][1] + seg[1][1]) / 2]
}

/**
 * Given the two long-edge candidates and a reference point inside the room
 * (compartment centroid), pick the edge whose midpoint is CLOSER to the
 * reference. That edge is the **room-side** edge of the sill — the long edge
 * facing the room interior (closer to the centroid).
 *
 * Falls back to the first candidate if the reference point is missing.
 *
 * @param {[[number, number], [number, number]][]} candidates
 * @param {[number, number] | null} referencePoint — compartment centroid
 * @returns {[[number, number], [number, number]] | null}
 */
export function pickRoomEdge(candidates, referencePoint) {
  if (!candidates || candidates.length === 0) return null
  if (candidates.length === 1 || !referencePoint) return candidates[0]
  let best = candidates[0]
  let bestDist = Infinity
  for (const seg of candidates) {
    const m = _midpoint(seg)
    const dx = m[0] - referencePoint[0]
    const dy = m[1] - referencePoint[1]
    const dist = dx * dx + dy * dy
    if (dist < bestDist) {
      bestDist = dist
      best = seg
    }
  }
  return best
}

/**
 * Picks the **facade-side** edge — the long edge of the sill on the facade
 * side (FARTHER from the compartment centroid). This is the edge from which
 * the sun-patch parallelogram is extruded into the room.
 *
 * @param {[[number, number], [number, number]][]} candidates
 * @param {[number, number] | null} referencePoint — compartment centroid
 * @returns {[[number, number], [number, number]] | null}
 */
export function pickFacadeEdge(candidates, referencePoint) {
  if (!candidates || candidates.length === 0) return null
  if (candidates.length === 1 || !referencePoint) return candidates[0]
  let best = candidates[0]
  let bestDist = -Infinity
  for (const seg of candidates) {
    const m = _midpoint(seg)
    const dx = m[0] - referencePoint[0]
    const dy = m[1] - referencePoint[1]
    const dist = dx * dx + dy * dy
    if (dist > bestDist) {
      bestDist = dist
      best = seg
    }
  }
  return best
}

/**
 * @deprecated Legacy name from when "outer" meant "outer relative to centroid".
 * That happens to be the facade side. Use {@link pickFacadeEdge} for clarity.
 */
export const pickOuterEdge = pickFacadeEdge

/**
 * Build the four-point parallelogram representing the sun cone for one
 * window segment, in Y-UP cartesian space.
 *
 * Source segment: typically the **facade edge** of the sill (the long edge on
 * the facade side). Extruding from the facade edge produces a parallelogram
 * that naturally covers both the sill thickness and the room interior in one
 * shape — clipping (task 2.2) to (compartment path ∪ all its sills) then
 * trims the final visible patch correctly, including:
 *   - sun-jamb shadowing (cut by the sill itself on the sun side)
 *   - neighbour-room spillover (cut by the compartment polygon)
 *   - inner walls / column intrusions (cut by the compartment polygon)
 *
 * @param {[[number, number], [number, number]]} segment — typically the facadeEdge
 * @param {number} angleDeg — already in Y-UP space (see {@link getDirectionalSunAngle})
 * @param {number} distance
 * @returns {[number, number][]}
 */
export function buildSunPatchPolygon(segment, angleDeg, distance) {
  if (!segment) return []
  const [p1, p2] = segment
  const rad = _toRadians(angleDeg)
  const dx = distance * Math.cos(rad)
  const dy = distance * Math.sin(rad)
  return [
    [p1[0],       p1[1]],
    [p2[0],       p2[1]],
    [p2[0] + dx,  p2[1] + dy],
    [p1[0] + dx,  p1[1] + dy],
  ]
}

export function polygonToPath(points) {
  if (!points || points.length === 0) return ''
  let d = `M ${points[0][0]} ${points[0][1]}`
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i][0]} ${points[i][1]}`
  }
  return `${d} Z`
}

/**
 * objectBoundingBox `<linearGradient>` coordinates for a Y-UP angle.
 * (0,0) of the gradient = transparent / window side; (1,1) = opaque / room side.
 */
export function gradientCoordinates(angleDeg) {
  const rad = _toRadians(angleDeg)
  const x = Math.cos(rad)
  const y = Math.sin(rad) // Y-UP
  // SVG objectBoundingBox is Y-DOWN — flip y:
  return {
    x1: (1 - x) / 2,
    y1: (1 + y) / 2,
    x2: (1 + x) / 2,
    y2: (1 - y) / 2,
  }
}

// ── Window helpers ───────────────────────────────────────────────────────────

export function normalizeIdHex(value) {
  return String(value ?? '').trim().toUpperCase()
}

/**
 * Filter + cast `sun_light_ranges` to a clean `[start, end][]` array.
 */
export function normalizeSunLightRanges(ranges) {
  if (!Array.isArray(ranges)) return []
  const out = []
  for (const r of ranges) {
    if (!Array.isArray(r) || r.length < 2) continue
    const a = Number(r[0])
    const b = Number(r[1])
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue
    if (b < a) continue
    out.push([a, b])
  }
  return out
}

/**
 * Is a window currently lit at the given slider angle?
 */
export function isWindowIlluminatedAtAngle(windowOpening, angleDeg) {
  const ranges = windowOpening?.sun_light_ranges ?? []
  if (ranges.length === 0) return false
  const hour = angleToHour(angleDeg)
  return ranges.some((r) => hour >= Number(r[0]) && hour <= Number(r[1]))
}
